<template lang="pug">
  FormBlock(mobile).b-project-members
    template(#header)
      ._header
        ._title {{ title }}
        ._col(v-if="project.isTemplate")
        ._col(v-for="role in Object.values(roles)") {{ role }}
    ._items
      ._item.-author
        Icon(name="crown")
        UserTile._user(:user="project.author")
        Btn(green v-if="$store.state.auth.user.id === project.author.id" @click="changeAuthor") Изменить
      ._item(v-for="item in members" :class="{ adding: !item.id }")
        UserTile._user(:user="item.user")
        div(v-if="project.isTemplate")
        label._radio-label(v-for="(label, role) in roles" @click="updateMember(item, role)")
          BaseRadio(:checked="item.role === role")
          ._radio-title {{ label }}
        ._delete(@click="deleteMember(item)")
    ._footer
      Btn(xl dark narrow @click="selectUsersFromContacts")
        Icon(name="plus" pointer)
        | Добавить из контактов
      Btn(xl dark narrow @click="selectUsersFromDepartment" v-if="project.companyDepartment")
        Icon(name="plus" pointer)
        | Добавить из отдела
      Btn(xl dark narrow @click="selectUsersFromCompany" v-if="project.companyDepartment")
        Icon(name="plus" pointer)
        | Добавить из компании
      RequestIndicator._indicator(:loading="$store.state.project.requestCounter > 0")
    UserSelectPopup2(ref="userSelectPopup")
</template>

<script>
import companyEmployeeApi from '@/api/companyEmployee';
import Access from '@/utils/access';

import FormBlock from '@/components/FormBlock.vue';
import UserSelectPopup2 from '@/components/UserSelectPopup2.vue';
import UserTile from '@/components/UserTile.vue';
import BaseRadio from '@/components/BaseRadio.vue';

export default {
  name: 'ProjectMembers',
  components: { BaseRadio, UserTile, UserSelectPopup2, FormBlock }, // eslint-disable-line
  data() {
    return {
      companyEmployees: {},
    };
  },
  computed: {
    currUser() { return this.$store.state.auth.user; },
    access() { return new Access(this.project, this.currUser); },
    project() { return this.$store.state.project.project ?? {}; },
    isTemplate() { return this.project?.isTemplate; },
    title() { return this.isTemplate ? 'Текущие участники шаблона' : 'Текущие участники проекта'; },
    members() { return this.project.projectMembers; },
    roles() {
      return this.project?.isTemplate
        ? { work: 'Рабочий', admin: 'Админ' }
        : { standard: 'Стандартный', work: 'Рабочий', admin: 'Админ' };
    },
  },
  methods: {
    async selectUsersFromContacts() {
      const usersBeforeSelect = this.members.map((m) => m.user['@id']);
      const selectedUsers = await this.$refs.userSelectPopup.open(usersBeforeSelect, true);

      if (selectedUsers) this.addUsers(selectedUsers, usersBeforeSelect);
    },
    async selectUsersFromDepartment() {
      const usersBeforeSelect = this.members.map((m) => m.user['@id']);
      const usersToSelect = this.project.companyDepartment.departmentMembers.map(m => m.employee.user);
      const selectedUsers = await this.$refs.userSelectPopup.open(usersBeforeSelect, true, usersToSelect, 'Сотрудники отдела', 'Нет сотрудников отдела');

      if (selectedUsers) this.addUsers(selectedUsers, usersBeforeSelect);
    },
    async selectUsersFromCompany() {
      const company = this.project.companyDepartment.company;

      if (!this.companyEmployees.length) {
        const r = await companyEmployeeApi.fetchList({ company: company.id }, true);
        this.companyEmployees = r.items;
      }

      const usersBeforeSelect = this.members.map((m) => m.user['@id']);
      const usersToSelect = [company.owner, ...this.companyEmployees.map(m => m.user)];
      const selectedUsers = await this.$refs.userSelectPopup.open(usersBeforeSelect, true, usersToSelect, 'Сотрудники компании', 'Нет сотрудников компании');

      if (selectedUsers) this.addUsers(selectedUsers, usersBeforeSelect);
    },
    addUsers(selectedUsers, usersBeforeSelect) {
      selectedUsers.forEach((user) => {
        if (usersBeforeSelect.includes(user['@id'])) return;
        const role = Object.keys(this.roles)[0];
        this.$store.dispatch('project/addMember', { user, role });
      });
    },
    updateMember(member, role) {
      if (!member.id) return;
      this.$store.dispatch('project/updateMemberRole', { member, role });
    },
    async deleteMember(member) {
      const question = `Вы действительно хотите исключить пользователя [${member.user.name} ${member.user.surname}] из проекта?`;
      if (await this.$confirm(question)) {
        await this.$store.dispatch('project/deleteMember', member);
      }
    },

    async changeAuthor() {
      const question = 'Вы действительно хотите установить нового автора проекта? Эту операцию невозможно отменить.';
      if (!await this.$confirm(question)) return;

      const newAuthor = await this.$refs.userSelectPopup.open(this.project.author['@id'], false, this.members.map(m => m.user), 'Участники проекта', 'Участников проекта не найдено');

      if (!newAuthor) return;

      this.$store.dispatch('project/addMember', { user: this.project.author, role: 'admin' });
      this.$store.commit('project/removeMember', this.members.find(m => m.user.id === newAuthor.id));
      this.$store.dispatch('project/updateAuthor', newAuthor);
    },
  },
};
</script>

<style scoped lang="scss">
  .project-members {
    &__title {
      font-size: 18px;
      line-height: 24px;
      color: var(--color-super-dark);
    }

    &__header {
      display: grid;
      grid-template-columns: 1fr 90px 90px 90px 28px;
      column-gap: 10px;
      align-items: center;
      padding-right: 20px;
    }

    &__col {
      font-size: 14px;
      line-height: 24px;
      text-align: center;
      color: #788CA1;
    }

    &__items {
      margin-bottom: 30px;
    }

    &__item {
      padding: 7px 20px 7px 20px;
      display: grid;
      grid-template-columns: 1fr 90px 90px 90px 28px;
      column-gap: 10px;
      align-items: center;
      justify-items: center;

      &.adding {
        opacity: .7;
      }

      &:nth-child(2n+1) {
        background: #F5F7F9;
        border-radius: 6px;
      }

      &--author {
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: relative;
        flex-wrap: wrap;

        svg {
          position: absolute;
          z-index: 10;
          top: 4px;
          left: 20px;
        }
      }
    }

    &__user {
      justify-self: start;
    }

    &__delete {
      display: none;
      width: 22px;
      height: 22px;
      position: relative;
      cursor: pointer;

      &:before, &:after {
        position: absolute;
        top: 50%;
        left: 0;
        display: block;
        content: '';
        width: 100%;
        height: 1px;
        background: #C4C4C4;
      }

      &:before { transform: rotate(45deg); }
      &:after { transform: rotate(-45deg); }
    }

    &__item:hover &__delete {
      display: block;
    }

    &__radio-title {
      display: none;
    }

    &__user-to-delete {
      color: var(--color-accent);
    }

    &__footer {
      display: flex;
      //justify-content: space-between;
      gap: 30px;
      flex-wrap: wrap;
      align-items: center;
    }

    &__indicator {
      align-self: flex-end;
      margin-left: auto;
    }

    @media (max-width: 1500px) {
      &__header {
        grid-template-columns: 1fr 70px 70px 70px 22px;
        padding-right: 15px;
      }

      &__item {
        padding: 7px 15px;
        grid-template-columns: 1fr 70px 70px 70px 22px;
      }

      &__delete {
        display: block;
      }
    }

    @media (max-width: 650px) {
      &__header {
        grid-template-columns: 1fr;
      }

      &__col {
        display: none;
      }

      &__item {
        grid-template-columns: 1fr 22px;
        justify-items: start;
        gap: 15px;
        padding: 20px;

        &--author {
          grid-template-columns: 1fr;
        }
      }

      &__delete {
        grid-row: 1;
        grid-column: 2;
      }

      &__radio-label {
        display: flex;
        align-items: center;
        gap: 15px;
        grid-column: 1 / 2 span;
      }

      &__radio-title {
        display: block;
        font-size: 14px;
        color: #788CA1;
      }
    }
  }
</style>
